
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LucideIcon, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface MobileNavigationProps {
  items: {
    name: string;
    href: string;
    icon: LucideIcon;
    badge?: {
      count: number;
      color?: string;
    };
    subItems?: { name: string; href: string }[];
  }[];
}

export const MobileNavigation = ({ items }: MobileNavigationProps) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const toggleExpand = (name: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  return (
    <nav className="space-y-1.5" aria-label="Mobile Navigation">
      {items.map((item) => {
        const isActive = location.pathname === item.href;
        const hasSubItems = item.subItems && item.subItems.length > 0;
        const isExpanded = expandedItems[item.name] || false;
        const Icon = item.icon;
        
        return (
          <div key={item.name} className="rounded-lg overflow-hidden">
            <div className="flex items-center">
              <Link
                to={hasSubItems ? "#" : item.href}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 flex-grow",
                  isActive
                    ? "bg-gradient-to-r from-emerald-500/10 to-sky-500/10 text-emerald-600 font-medium shadow-sm"
                    : "text-gray-700 hover:bg-gray-100/70 hover:text-emerald-600"
                )}
                onClick={hasSubItems ? (e) => {
                  e.preventDefault();
                  toggleExpand(item.name);
                } : undefined}
                aria-current={isActive ? "page" : undefined}
              >
                <Icon 
                  className={cn(
                    "w-5 h-5", 
                    isActive ? "text-emerald-600" : "text-gray-500"
                  )} 
                  aria-hidden="true"
                />
                <span className="text-sm">{item.name}</span>
                
                {item.badge && item.badge.count > 0 && (
                  <span className={cn(
                    "ml-auto px-2 py-0.5 text-xs font-medium rounded-full",
                    item.badge.color || "bg-emerald-100 text-emerald-600"
                  )}>
                    {item.badge.count}
                  </span>
                )}
                
                {hasSubItems && (
                  <div className="ml-auto">
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                )}
              </Link>
            </div>
            
            {hasSubItems && isExpanded && (
              <div className="ml-10 pl-2 border-l border-gray-200 space-y-1 mt-1 mb-2">
                {item.subItems!.map((subItem) => {
                  const isSubActive = location.pathname === subItem.href;
                  
                  return (
                    <Link
                      key={subItem.name}
                      to={subItem.href}
                      className={cn(
                        "block py-2 px-3 text-sm rounded-lg transition-all duration-200",
                        isSubActive
                          ? "text-emerald-600 bg-emerald-50 font-medium"
                          : "text-gray-600 hover:text-emerald-600 hover:bg-gray-50"
                      )}
                    >
                      {subItem.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};
